// The plain-language reason for a native queue failure code (BL-2685), and the codes Retry cannot
// cure. Pure: the Storage & Sync page never prints a raw code or the engine's raw message.

/** What an unknown code reads as. The code itself is reported, not shown. */
export const UNKNOWN_FAILURE_REASON = 'This item could not be sent.';

const REASONS: Readonly<Record<string, string>> = {
  STAGED_FILE_MISSING: 'The file for this item is no longer on this phone.',
  FILE_WRITE_FAILED: 'The file could not be saved on this phone.',
  OUT_OF_SPACE: 'This phone is out of storage.',
  PAYLOAD_INVALID: 'This item is not in a form the server accepts.',
  INVALID_PAYLOAD: 'This item is not in a form the server accepts.',
  PHOTO_TOO_LARGE: 'This photo is too large to upload.',
  THUMBNAIL_TOO_LARGE: 'This photo preview is too large to upload.',
  REQUEST_REFUSED: 'The server refused this item.',
  REQUEST_FAILED: 'The request did not reach the server.',
  RETRIES_EXHAUSTED: 'It could not be sent after several tries.',
  DESTINATION_LOST: 'The lot this item belongs to is no longer available.',
  PARENT_FAILED: 'The lot this item belongs to did not sync.',
  PARENT_NOT_FOUND: 'The lot this item belongs to is no longer available.',
  MEDIA_REMOVED: 'This photo was removed.',
  ID_IN_USE: 'The server already has an item with this ID.',
  STALE_FIELDS: 'The server has a newer version of this lot.',
  RATE_LIMITED: 'The server asked this phone to slow down.',
  LANE_UNAVAILABLE: 'This kind of item cannot be sent from this version of the app.',
  LAP_LIMIT: 'It could not be sent after several tries.',
  LINK_RESPONSE_INVALID: 'The server did not give this phone a place to upload the photo.',
  NO_PROGRESS: 'The server did not return the photos it was asked for.',
  BODY_ASSEMBLY_FAILED: 'This phone could not prepare the item to send.',
  REPLY_TOO_LARGE: 'The answer from the server was too large for this phone.',
  MANIFEST_VERSION_UNSUPPORTED: 'This version of the app cannot read the answer from the server.',
  TAR_INCOMPLETE: 'The photo previews did not arrive complete.',
  ROW_GONE: 'This item was already removed from the queue.',
};

/** Codes where sending the same item again cannot succeed, so the row also offers Discard. */
const UNCURABLE: ReadonlySet<string> = new Set([
  'STAGED_FILE_MISSING',
  'PHOTO_TOO_LARGE',
  'THUMBNAIL_TOO_LARGE',
  'PAYLOAD_INVALID',
  'INVALID_PAYLOAD',
  'MANIFEST_VERSION_UNSUPPORTED',
  'MEDIA_REMOVED',
  'DESTINATION_LOST',
  'PARENT_FAILED',
  'PARENT_NOT_FOUND',
]);

/**
 * @param code - the engine's failure code, or null.
 * @param report - called with the code when it has no wording yet, so the gap reaches the logs.
 * @returns a sentence for the row, with no semicolons.
 */
export function failureReasonText(code: string | null, report?: (code: string | null) => void): string {
  if (code != null && REASONS[code] != null) return REASONS[code];
  if (code != null && /^HTTP_\d+$/.test(code)) return 'The server could not accept this item.';
  report?.(code);
  return UNKNOWN_FAILURE_REASON;
}

/**
 * @param code - the engine's failure code, or null.
 * @returns true when Retry cannot cure the failure (a missing file, an oversize photo).
 */
export function isUncurableByRetry(code: string | null): boolean {
  return code != null && UNCURABLE.has(code);
}
